import { useState } from 'react'
import { Op, FrameId } from './pipeline-types'
import { savePipelineView, updatePipelineView } from './saved-views'
import { SavedView } from './view-config'
import { SaveDialog } from './components/SaveDialog'

/** Extra context for an in-place overwrite of an existing saved pipeline report. */
type SaveOpts = {
  /** Pre-fill the name field (e.g. the current report's name for a rename). */
  defaultName?: string
  /** When set, offer "Save" (overwrite in place) — enables renaming this report. */
  existingId?: string
  /** Called with the updated record after an in-place overwrite, so the host can refresh. */
  onSaved?: (saved: SavedView) => void
}

type Pending = { ops: Op[]; frame: FrameId } & SaveOpts

/**
 * Branded-save wiring for pipeline-built reports. The PipelineBuilder's `onSave`
 * hands back the op-chain; `requestSave` stashes it and opens the SaveDialog so
 * the user names it in a First-Bite modal (not a browser prompt).
 *
 * Two paths, mirroring the ViewConfig report flow:
 *   - new report (no `existingId`): persist a fresh copy and route to it.
 *   - existing report (`existingId` set): offer "Save" to overwrite in place
 *     (this is what makes a pipeline report renamable) alongside "Save as new".
 *
 * @param navigate - router navigate fn (from useNavigate)
 * @returns requestSave to pass through to PipelineBuilder, and the dialog node
 */
export function usePipelineSave(navigate: (to: string) => void): {
  requestSave: (ops: Op[], frame: FrameId, opts?: SaveOpts) => void
  dialog: React.ReactNode
} {
  const [pending, setPending] = useState<Pending | null>(null)

  const requestSave = (ops: Op[], frame: FrameId, opts: SaveOpts = {}) =>
    setPending({ ops, frame, ...opts })

  const dialog = (
    <SaveDialog
      open={!!pending}
      onClose={() => setPending(null)}
      kind="report"
      defaultName={pending?.defaultName ?? ''}
      showDescription={false}
      canOverwrite={!!pending?.existingId}
      onOverwrite={({ name }) => {
        if (!pending?.existingId) return
        const saved = updatePipelineView(pending.existingId, {
          name,
          ops: pending.ops,
          frame: pending.frame,
        })
        if (saved) pending.onSaved?.(saved)
      }}
      onSaveNew={({ name }) => {
        if (!pending) return
        const saved = savePipelineView({
          name,
          ops: pending.ops,
          frame: pending.frame,
        })
        navigate(`/reporting/${saved.id}`)
      }}
    />
  )

  return { requestSave, dialog }
}
